<script setup lang="ts">
  import { computed } from "vue";
  import { Text } from "@ui-kit";
  import { OrderTypeIcon } from "@src/components";
  import { useOrderAck } from "@src/api";
  import { useProfileOptions } from "@src/hooks";
  import { useOrderListStore, useWidgetsStore, useExecutorListStore } from "@src/store";
  import { useOrderItemListState } from "./state";
  import { default as OrderCardDate } from "./OrderCardDate.vue";
  import { default as OrderCardRouteDirection } from "./OrderCardRouteDirection.vue";
  import { default as OrderCardComment } from "./OrderCardComment.vue";
  import { default as OrderCardItemCreated } from "./OrderCardItemCreated.vue";
  import { default as OrderCardExecuter } from "./OrderCardExecuter.vue";
  import { default as OrderCardItemList } from "./OrderCardItemList.vue";
  import { default as OrderCardItemListTitle } from "./OrderCardItemListTitle.vue";
  import { default as OrderCardCreatedFooter } from "./OrderCardCreatedFooter.vue";
  import { default as OrderCardStatus } from "./OrderCardStatus.vue";

  const { mutateAsync: orderAck, isPending: isOrderAckPending } = useOrderAck();

  const order_list_store = useOrderListStore();
  const order_item_list_state = useOrderItemListState();
  const widgets_store = useWidgetsStore();
  const { hasPermission } = useProfileOptions();
  const executorListStore = useExecutorListStore();

  const order = computed(() => order_list_store.map[widgets_store.order_card.order_id]);

  const handleOrderAck = async () => {
    if (!order.value) return;

    await orderAck({ order_id: order.value.order_id });
  };
</script>

<template>
  <div class="order-card-created" data-testid="order-card-created">
    <div class="content">
      <div class="info">
        <div class="status-date">
          <OrderCardStatus :status="order.status" :partial="order.partial" />
          <OrderCardDate :date="order.created" />
        </div>
        <div class="number-route-container">
          <div class="number-route">
            <Text font="heading-H5">{{ `№${order.order_number}` }}</Text>
            <OrderCardRouteDirection
              :order_place_from="order.place_from"
              :store="order.place_from_type"
              :order_place_section="order.place_section"
              :order_place_to="order.place_to"
            />
          </div>
          <OrderTypeIcon :type="order.order_type" :size="36" />
        </div>
        <OrderCardComment v-if="order.comment" :comment="order.comment" />
        <hr class="divider" />
        <OrderCardExecuter
          :display-name="
            order.status === 'picking' ? executorListStore.map[order.executed_by]?.display_name : undefined
          "
          :description="$t('Сборщик')"
        />
      </div>
      <OrderCardItemList>
        <OrderCardItemListTitle :totalItems="order.goods.length" />
        <OrderCardItemCreated
          v-for="item in order_item_list_state.value.list"
          :key="item.seriaId"
          :image_url="item.seria.image_url"
          :title="item.seria.title"
          :article="item.seria.article"
          :color="item.seria.color"
          :goodList="item.goods"
        />
      </OrderCardItemList>
      <OrderCardCreatedFooter
        v-if="hasPermission('mobile.order_ack') && order.status === 'request'"
        :is_loading="isOrderAckPending"
        @click:ack="handleOrderAck"
      />
    </div>
  </div>
</template>

<style scoped>
  .order-card-created {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    overflow-y: hidden;

    .content {
      display: flex;
      flex-direction: column;
      flex: 1;
      background-color: var(--sf-base-background-primary);
      overflow-y: auto;
    }

    .info {
      display: flex;
      flex-direction: column;
      padding: 0 16px 16px;
      gap: 8px;
      background-color: var(--sf-base-background-white);
      border-radius: 0 0 16px 16px;

      .status-date {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .number-route-container {
        display: flex;
        justify-content: space-between;
      }

      .number-route {
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .divider {
        margin: 8px 0 7px;
        border: 0;
        border-bottom: 1px solid var(--sf-base-border-primary);
      }
    }
  }
</style>
